import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Table,
  Card,
  Input,
  DatePicker,
  Select,
  Space,
  Button,
  Tag,
  Collapse,
  Drawer,
  Descriptions,
  Row,
  Col,
  Statistic,
  Dropdown,
  Tooltip,
  Avatar,
  Empty,
  Divider,
  Tabs,
  Spin,
  message,
  Layout,
} from "antd";
import MJ_LOGO from "@/assets/mnj-logo.jpg";
import { apiClient } from "@/api/client";
import AuthButton from "../auth/AuthButton";
import { EnvironmentBadge } from "../common/EnvironmentBadge";
import {
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  UserOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  SyncOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  HistoryOutlined,
  SwapOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";

const { Title, Text, Paragraph } = Typography;
const { Header: AntHeader } = Layout;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const { Option } = Select;

// Type definitions
interface AuditEntry {
  auditId: string;
  entityType: string;
  entityId: string;
  userId: string;
  userName: string;
  action: string;
  timestamp: string;
  oldValue: string;
  newValue: string;
  changeSummary: string;
  appContext: string;
  ipAddress: string;
  sessionId: string | null;
  metadata: Record<string, any>;
}

interface AuditApiResponse {
  entityType: string;
  entityId: string;
  totalCount: number;
  entries: AuditEntry[];
  continuationToken: string | null;
}

interface ParsedChange {
  fieldName: string;
  oldValue: any;
  newValue: any;
}

interface AuditRecord {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  userId: string;
  userName: string;
  timestamp: Date;
  ipAddress: string;
  sessionId: string | null;
  changeSummary: string;
  parsedOldValue: Record<string, any>;
  parsedNewValue: Record<string, any>;
  changes: ParsedChange[];
  metadata: Record<string, any>;
}

// API configuration
const APP_CONTEXT = "SiteOperationsWeb";

// Helper function to parse JSON values safely
const parseJsonSafe = (jsonString: string): Record<string, any> => {
  try {
    return JSON.parse(jsonString);
  } catch {
    return {};
  }
};

// Helper function to extract changes from old and new values
const extractChanges = (oldValue: string, newValue: string): ParsedChange[] => {
  const oldObj = parseJsonSafe(oldValue);
  const newObj = parseJsonSafe(newValue);
  const changes: ParsedChange[] = [];

  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

  allKeys.forEach((key) => {
    if (oldObj[key] !== newObj[key]) {
      changes.push({
        fieldName: key,
        oldValue: oldObj[key],
        newValue: newObj[key],
      });
    }
  });

  return changes;
};

// Transform API response to internal format
const transformApiResponse = (response: AuditApiResponse): AuditRecord[] => {
  return response.entries.map((entry) => ({
    id: entry.auditId,
    entityType: entry.entityType,
    entityId: entry.entityId,
    action: entry.action,
    userId: entry.userId,
    userName: entry.userName,
    timestamp: new Date(entry.timestamp),
    ipAddress: entry.ipAddress,
    sessionId: entry.sessionId,
    changeSummary: entry.changeSummary,
    parsedOldValue: parseJsonSafe(entry.oldValue),
    parsedNewValue: parseJsonSafe(entry.newValue),
    changes: extractChanges(entry.oldValue, entry.newValue),
    metadata: entry.metadata,
  }));
};

// Fetch audit data from API
const fetchAuditData = async (dateFrom?: string, dateTo?: string): Promise<AuditRecord[]> => {
  const response = await apiClient.get<AuditApiResponse>("/audit/search", {
    params: {
      appContext: APP_CONTEXT,
      pageSize: 50,
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
    },
  });

  return transformApiResponse(response.data);
};

const AuditTrailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<string[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<AuditRecord | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [comparisonRecords, setComparisonRecords] = useState<AuditRecord[]>([]);
  const [comparisonVisible, setComparisonVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Use React Query to fetch audit data
  const {
    data: auditData = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "auditTrail",
      selectedDateRange?.[0]?.format("YYYY-MM-DD"),
      selectedDateRange?.[1]?.format("YYYY-MM-DD"),
    ],
    queryFn: () =>
      fetchAuditData(
        selectedDateRange?.[0]?.format("YYYY-MM-DD"),
        selectedDateRange?.[1]?.format("YYYY-MM-DD")
      ),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Show error message if query fails
  React.useEffect(() => {
    if (error) {
      message.error("Failed to load audit data");
    }
  }, [error]);

  // Quick date filters
  const datePresets = [
    { label: "Today", value: [dayjs().startOf("day"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
    { label: "Last 7 days", value: [dayjs().subtract(7, "day"), dayjs()] as [Dayjs, Dayjs] },
    { label: "Last 30 days", value: [dayjs().subtract(30, "day"), dayjs()] as [Dayjs, Dayjs] },
    { label: "Last 90 days", value: [dayjs().subtract(90, "day"), dayjs()] as [Dayjs, Dayjs] },
  ];

  // Extract unique values for filters
  const uniqueUsers = useMemo(() => {
    const userMap = new Map<string, string>();
    auditData.forEach((record) => {
      userMap.set(record.userId, record.userName);
    });
    return Array.from(userMap.entries()).map(([id, name]) => ({ id, name }));
  }, [auditData]);

  const uniqueActions = useMemo(() => {
    const actionSet = new Set(auditData.map((record) => record.action));
    return Array.from(actionSet);
  }, [auditData]);

  const uniqueEntityTypes = useMemo(() => {
    const typeSet = new Set(auditData.map((record) => record.entityType));
    return Array.from(typeSet);
  }, [auditData]);

  // Filter data
  const filteredData = useMemo(() => {
    return auditData.filter((record) => {
      // Search filter
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const matchesSearch =
          record.entityType.toLowerCase().includes(searchLower) ||
          record.action.toLowerCase().includes(searchLower) ||
          record.userName.toLowerCase().includes(searchLower) ||
          record.entityId.toLowerCase().includes(searchLower) ||
          record.changeSummary.toLowerCase().includes(searchLower) ||
          record.changes.some((change) =>
            change.fieldName.toLowerCase().includes(searchLower)
          );
        if (!matchesSearch) return false;
      }

      // Date range filter (already handled by API, but can be used for client-side refinement)
      if (selectedDateRange && selectedDateRange[0] && selectedDateRange[1]) {
        const recordDate = dayjs(record.timestamp);
        if (
          !recordDate.isAfter(selectedDateRange[0].startOf("day")) ||
          !recordDate.isBefore(selectedDateRange[1].endOf("day"))
        ) {
          return false;
        }
      }

      // User filter
      if (selectedUsers.length > 0 && !selectedUsers.includes(record.userId)) {
        return false;
      }

      // Action filter
      if (selectedActions.length > 0 && !selectedActions.includes(record.action)) {
        return false;
      }

      // Entity type filter
      if (selectedEntityTypes.length > 0 && !selectedEntityTypes.includes(record.entityType)) {
        return false;
      }

      return true;
    });
  }, [auditData, searchText, selectedDateRange, selectedUsers, selectedActions, selectedEntityTypes]);

  // Statistics
  const statistics = useMemo(() => {
    return {
      total: filteredData.length,
      today: filteredData.filter((r) => dayjs(r.timestamp).isAfter(dayjs().startOf("day"))).length,
      thisWeek: filteredData.filter((r) => dayjs(r.timestamp).isAfter(dayjs().subtract(7, "day"))).length,
      criticalChanges: 0, // Can be computed based on specific business rules
    };
  }, [filteredData]);

  const getActionIcon = (action: string) => {
    const actionLower = action.toLowerCase();
    const icons: Record<string, React.ReactNode> = {
      create: <PlusOutlined />,
      created: <PlusOutlined />,
      update: <SyncOutlined />,
      updated: <SyncOutlined />,
      delete: <DeleteOutlined />,
      deleted: <DeleteOutlined />,
      approve: <CheckOutlined />,
      approved: <CheckOutlined />,
      reject: <CloseOutlined />,
      rejected: <CloseOutlined />,
      lock: <ClockCircleOutlined />,
      unlock: <ClockCircleOutlined />,
      view: <EyeOutlined />,
      export: <DownloadOutlined />,
    };
    return icons[actionLower] || <FileTextOutlined />;
  };

  const getActionColor = (action: string) => {
    const actionLower = action.toLowerCase();
    const colors: Record<string, string> = {
      create: "green",
      created: "green",
      update: "blue",
      updated: "blue",
      delete: "red",
      deleted: "red",
      approve: "cyan",
      approved: "cyan",
      reject: "orange",
      rejected: "orange",
      lock: "purple",
      unlock: "purple",
      view: "default",
      export: "geekblue",
    };
    return colors[actionLower] || "default";
  };

  const getEntityTypeColor = (entityType: string) => {
    const typeLower = entityType.toLowerCase();
    const colors: Record<string, string> = {
      project: "blue",
      forecast: "cyan",
      revision: "green",
      rate: "orange",
      lock: "purple",
      permission: "magenta",
      user: "gold",
    };
    return colors[typeLower] || "default";
  };

  const handleViewDetails = (record: AuditRecord) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  const handleExport = (format: string) => {
    console.log(`Exporting as ${format}`);
    message.info(`Export as ${format} - Feature coming soon`);
  };

  const handleCompare = () => {
    if (comparisonRecords.length === 2) {
      setComparisonVisible(true);
    }
  };

  const handleRowSelection = {
    selectedRowKeys: comparisonRecords.map((r) => r.id),
    onChange: (_selectedRowKeys: React.Key[], selectedRows: AuditRecord[]) => {
      if (selectedRows.length <= 2) {
        setComparisonRecords(selectedRows);
      }
    },
    getCheckboxProps: (record: AuditRecord) => ({
      disabled: comparisonRecords.length === 2 && !comparisonRecords.some((r) => r.id === record.id),
    }),
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setSelectedDateRange(dates);
    // React Query will automatically refetch when the queryKey changes
  };

  const handleRefresh = () => {
    refetch();
  };

  const columns: ColumnsType<AuditRecord> = [
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
      render: (timestamp: Date) => (
        <Space direction="vertical" size={0}>
          <Text>{dayjs(timestamp).format("MMM DD, YYYY")}</Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {dayjs(timestamp).format("HH:mm:ss")}
          </Text>
        </Space>
      ),
    },
    {
      title: "User",
      dataIndex: "userName",
      key: "userName",
      width: 200,
      render: (userName: string, record: AuditRecord) => (
        <Space>
          <Avatar icon={<UserOutlined />} size="small" />
          <Space direction="vertical" size={0}>
            <Text strong>{userName}</Text>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.userId}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 120,
      render: (action: string) => (
        <Tag icon={getActionIcon(action)} color={getActionColor(action)}>
          {action.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Entity Type",
      dataIndex: "entityType",
      key: "entityType",
      width: 120,
      render: (entityType: string) => (
        <Tag color={getEntityTypeColor(entityType)}>{entityType.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Entity ID",
      dataIndex: "entityId",
      key: "entityId",
      width: 150,
      render: (entityId: string) => (
        <Text code style={{ fontSize: "12px" }}>
          {entityId}
        </Text>
      ),
    },
    {
      title: "Changes",
      dataIndex: "changes",
      key: "changes",
      width: 100,
      render: (changes: ParsedChange[]) => (
        <Tag color="blue">{changes.length} field(s)</Tag>
      ),
    },
    {
      title: "Summary",
      dataIndex: "changeSummary",
      key: "changeSummary",
      ellipsis: true,
      render: (summary: string) => (
        <Tooltip title={summary}>
          <Text style={{ fontSize: "12px" }}>{summary}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Action",
      key: "action-btn",
      width: 100,
      fixed: "right",
      render: (_: any, record: AuditRecord) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          Details
        </Button>
      ),
    },
  ];

  const exportMenuItems = [
    {
      key: "pdf",
      label: "Export as PDF",
      icon: <DownloadOutlined />,
      onClick: () => handleExport("pdf"),
    },
    {
      key: "excel",
      label: "Export as Excel",
      icon: <DownloadOutlined />,
      onClick: () => handleExport("excel"),
    },
    {
      key: "csv",
      label: "Export as CSV",
      icon: <DownloadOutlined />,
      onClick: () => handleExport("csv"),
    },
  ];

  const renderChangeComparison = (change: ParsedChange) => {
    const isNumeric = typeof change.newValue === "number" || typeof change.oldValue === "number";

    return (
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Text strong>{change.fieldName.charAt(0).toUpperCase() + change.fieldName.slice(1)}</Text>
          </Col>
          <Col span={8}>
            <div
              style={{
                padding: "8px",
                backgroundColor: "#fafafa",
                borderRadius: "4px",
              }}
            >
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Old Value
              </Text>
              <br />
              <Text delete>
                {isNumeric && change.oldValue
                  ? `$${Number(change.oldValue).toLocaleString()}`
                  : String(change.oldValue || "N/A")}
              </Text>
            </div>
          </Col>
          <Col span={2} style={{ textAlign: "center" }}>
            <SwapOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
          </Col>
          <Col span={8}>
            <div
              style={{
                padding: "8px",
                backgroundColor: "#f6ffed",
                borderRadius: "4px",
              }}
            >
              <Text type="secondary" style={{ fontSize: "12px" }}>
                New Value
              </Text>
              <br />
              <Text strong style={{ color: "#52c41a" }}>
                {isNumeric && change.newValue
                  ? `$${Number(change.newValue).toLocaleString()}`
                  : String(change.newValue || "N/A")}
              </Text>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <>
      {/* Navigation Bar */}
      <AntHeader
        className="bg-colorPrimary"
        style={{
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          padding: "12px 24px",
          height: "88px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            height: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <img
              src={MJ_LOGO}
              alt="M&J Evans Logo"
              onClick={() => navigate("/opportunities")}
              style={{
                height: "64px",
                width: "auto",
                objectFit: "contain",
                cursor: "pointer",
              }}
            />
            <div
              style={{
                width: "1px",
                height: "48px",
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              }}
            />
            <Title
              level={3}
              style={{
                margin: 0,
                color: "white",
                fontWeight: 600,
                fontSize: "20px",
              }}
            >
              Audit Trail
            </Title>
            <EnvironmentBadge mode={import.meta.env.MODE} variant="dark" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <AuthButton />
          </div>
        </div>
      </AntHeader>

      <div style={{ padding: "24px", backgroundColor: "#f5f5f5", minHeight: "calc(100vh - 88px)" }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Title level={3} style={{ margin: 0, lineHeight: 1 }}>
              <HistoryOutlined /> Audit Trail
            </Title>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/opportunities")}
              style={{ display: "flex", alignItems: "center" }}
            >
              Back to Dashboard
            </Button>
          </div>
          <Text type="secondary">
            Comprehensive audit history with filtering and comparison capabilities
          </Text>
        </div>

        {/* Statistics Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Records"
                value={statistics.total}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Today"
                value={statistics.today}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Last 7 Days"
                value={statistics.thisWeek}
                prefix={<SyncOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Critical Changes"
                value={statistics.criticalChanges}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Input
                  placeholder="Search audit trail..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={24} md={8}>
                <RangePicker
                  style={{ width: "100%" }}
                  value={selectedDateRange}
                  onChange={handleDateRangeChange}
                  presets={datePresets}
                  format="YYYY-MM-DD"
                />
              </Col>
              <Col xs={24} md={8}>
                <Space>
                  <Button
                    icon={<SyncOutlined />}
                    onClick={handleRefresh}
                    loading={isLoading}
                  >
                    Refresh
                  </Button>
                  <Button
                    icon={<FilterOutlined />}
                    onClick={() => {
                      setSearchText("");
                      setSelectedDateRange(null);
                      setSelectedUsers([]);
                      setSelectedActions([]);
                      setSelectedEntityTypes([]);
                    }}
                  >
                    Clear Filters
                  </Button>
                  <Dropdown menu={{ items: exportMenuItems }}>
                    <Button icon={<DownloadOutlined />}>Export</Button>
                  </Dropdown>
                  <Button
                    icon={<SwapOutlined />}
                    disabled={comparisonRecords.length !== 2}
                    onClick={handleCompare}
                    type={comparisonRecords.length === 2 ? "primary" : "default"}
                  >
                    Compare ({comparisonRecords.length}/2)
                  </Button>
                </Space>
              </Col>
            </Row>

            <Collapse ghost>
              <Panel header="Advanced Filters" key="1">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                      User
                    </Text>
                    <Select
                      mode="multiple"
                      style={{ width: "100%" }}
                      placeholder="Select users"
                      value={selectedUsers}
                      onChange={setSelectedUsers}
                      allowClear
                    >
                      {uniqueUsers.map((user) => (
                        <Option key={user.id} value={user.id}>
                          {user.name}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} md={8}>
                    <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                      Action Type
                    </Text>
                    <Select
                      mode="multiple"
                      style={{ width: "100%" }}
                      placeholder="Select actions"
                      value={selectedActions}
                      onChange={setSelectedActions}
                      allowClear
                    >
                      {uniqueActions.map((action) => (
                        <Option key={action} value={action}>
                          {action.toUpperCase()}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} md={8}>
                    <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                      Entity Type
                    </Text>
                    <Select
                      mode="multiple"
                      style={{ width: "100%" }}
                      placeholder="Select entity types"
                      value={selectedEntityTypes}
                      onChange={setSelectedEntityTypes}
                      allowClear
                    >
                      {uniqueEntityTypes.map((type) => (
                        <Option key={type} value={type}>
                          {type.toUpperCase()}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
              </Panel>
            </Collapse>
          </Space>
        </Card>

        {/* Audit Table */}
        <Card>
          <Spin spinning={isLoading}>
            <Table
              columns={columns}
              dataSource={filteredData}
              rowKey="id"
              rowSelection={handleRowSelection}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: filteredData.length,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
                pageSizeOptions: ["10", "25", "50", "100"],
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
              }}
              scroll={{ x: 1400 }}
              size="small"
              locale={{
                emptyText: (
                  <Empty
                    description="No audit records found"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </Spin>
        </Card>

        {/* Details Drawer */}
        <Drawer
          title={
            <Space>
              <HistoryOutlined />
              <span>Audit Record Details</span>
            </Space>
          }
          placement="right"
          width={720}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
        >
          {selectedRecord && (
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              {/* Basic Info */}
              <Card title="Basic Information" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Audit ID">
                    <Text code>{selectedRecord.id}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Timestamp">
                    {dayjs(selectedRecord.timestamp).format("MMMM DD, YYYY HH:mm:ss")}
                  </Descriptions.Item>
                  <Descriptions.Item label="User">
                    <Space>
                      <Avatar icon={<UserOutlined />} size="small" />
                      <div>
                        <Text strong>{selectedRecord.userName}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {selectedRecord.userId}
                        </Text>
                      </div>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Action">
                    <Tag
                      icon={getActionIcon(selectedRecord.action)}
                      color={getActionColor(selectedRecord.action)}
                    >
                      {selectedRecord.action.toUpperCase()}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Entity">
                    <Space>
                      <Tag color={getEntityTypeColor(selectedRecord.entityType)}>
                        {selectedRecord.entityType.toUpperCase()}
                      </Tag>
                      <Text code>{selectedRecord.entityId}</Text>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Session ID">
                    <Text code style={{ fontSize: "12px" }}>
                      {selectedRecord.sessionId || "N/A"}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="IP Address">
                    {selectedRecord.ipAddress}
                  </Descriptions.Item>
                  <Descriptions.Item label="Change Summary">
                    {selectedRecord.changeSummary}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* Changes */}
              <Card title={`Field Changes (${selectedRecord.changes.length})`} size="small">
                {selectedRecord.changes.length > 0 ? (
                  selectedRecord.changes.map((change, index) => (
                    <div key={index}>
                      {renderChangeComparison(change)}
                      {index < selectedRecord.changes.length - 1 && <Divider />}
                    </div>
                  ))
                ) : (
                  <Empty description="No field changes detected" />
                )}
              </Card>

              {/* Metadata */}
              {selectedRecord.metadata && Object.keys(selectedRecord.metadata).length > 0 && (
                <Card title="Additional Information" size="small">
                  <Descriptions column={1} size="small">
                    {Object.entries(selectedRecord.metadata).map(([key, value]) => (
                      <Descriptions.Item key={key} label={key}>
                        {typeof value === "object" ? JSON.stringify(value) : String(value)}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                </Card>
              )}
            </Space>
          )}
        </Drawer>

        {/* Comparison Drawer */}
        <Drawer
          title={
            <Space>
              <SwapOutlined />
              <span>Compare Audit Records</span>
            </Space>
          }
          placement="right"
          width={900}
          onClose={() => setComparisonVisible(false)}
          open={comparisonVisible}
        >
          {comparisonRecords.length === 2 && (
            <Tabs
              defaultActiveKey="1"
              items={[
                {
                  key: "1",
                  label: "Side by Side",
                  children: (
                    <Row gutter={16}>
                      {comparisonRecords.map((record, idx) => (
                        <Col span={12} key={record.id}>
                          <Card
                            title={`Record ${idx + 1}`}
                            size="small"
                            style={{ marginBottom: 16 }}
                          >
                            <Descriptions column={1} size="small">
                              <Descriptions.Item label="Timestamp">
                                {dayjs(record.timestamp).format("MMM DD, YYYY HH:mm:ss")}
                              </Descriptions.Item>
                              <Descriptions.Item label="User">{record.userName}</Descriptions.Item>
                              <Descriptions.Item label="Action">
                                <Tag color={getActionColor(record.action)}>{record.action}</Tag>
                              </Descriptions.Item>
                              <Descriptions.Item label="Changes">
                                {record.changes.length} field(s)
                              </Descriptions.Item>
                            </Descriptions>
                            <Divider />
                            <Text strong>Changes:</Text>
                            {record.changes.map((change, changeIdx) => (
                              <div key={changeIdx} style={{ marginTop: 8 }}>
                                <Text>{change.fieldName}:</Text>
                                <br />
                                <Text delete type="secondary" style={{ fontSize: "12px" }}>
                                  {String(change.oldValue)}
                                </Text>
                                {" → "}
                                <Text strong style={{ fontSize: "12px" }}>
                                  {String(change.newValue)}
                                </Text>
                              </div>
                            ))}
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  ),
                },
                {
                  key: "2",
                  label: "Differences",
                  children: (
                    <Card>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Text strong>Time Difference:</Text>
                        <Paragraph>
                          {Math.abs(
                            dayjs(comparisonRecords[0].timestamp).diff(
                              comparisonRecords[1].timestamp,
                              "day"
                            )
                          )}{" "}
                          days apart
                        </Paragraph>

                        <Divider />

                        <Text strong>Action Comparison:</Text>
                        <Space>
                          <Tag color={getActionColor(comparisonRecords[0].action)}>
                            {comparisonRecords[0].action}
                          </Tag>
                          <Text>vs</Text>
                          <Tag color={getActionColor(comparisonRecords[1].action)}>
                            {comparisonRecords[1].action}
                          </Tag>
                        </Space>

                        <Divider />

                        <Text strong>Users:</Text>
                        <Space direction="vertical">
                          <Text>
                            Record 1: {comparisonRecords[0].userName} ({comparisonRecords[0].userId}
                            )
                          </Text>
                          <Text>
                            Record 2: {comparisonRecords[1].userName} ({comparisonRecords[1].userId}
                            )
                          </Text>
                        </Space>
                      </Space>
                    </Card>
                  ),
                },
              ]}
            />
          )}
        </Drawer>
      </div>
    </>
  );
};

export default AuditTrailPage;
